import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { of } from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';
import { AuthService } from 'services/auth.service';
import { PermissionService } from 'services/user-management/permission.service';

export const authGuard: CanActivateFn = (route, _state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const permissionsService = inject(PermissionService);

  const loggedIn = authService.isLoggedIn();
  if (!loggedIn) {
    authService.storeRoute();
    console.debug('Auth guard redirecting to landing page');
    return router.createUrlTree(['/']);
  } else if (route.data) {
    return permissionsService.userPermissions.pipe(
      skipWhile((perms) => perms === undefined),
      map((perms) => {
        if (hasPermission(route.data.requiredPermissions, perms)) {
          return true;
        } else {
          return router.createUrlTree(['**']);
        }
      }),
    );
  }
  return of(true);
};

interface RequiredPermissions {
  some?: string[];
  all?: string[];
}

function hasPermission(
  permissionsRequired: RequiredPermissions,
  permissionsAvailable: string[],
): boolean {
  if (!permissionsRequired) {
    return true;
  }

  let hasRequiredAllPermissions = true;
  let hasRequiredAnyPermissions = true;
  if (permissionsRequired.all) {
    const matchedPermissions = permissionsRequired.all.filter((p) =>
      permissionsAvailable.includes(p),
    );

    if (matchedPermissions.length < permissionsRequired.all.length) {
      hasRequiredAllPermissions = false;
    } else {
      // No easy method to compare arrays in JS so sort both alphabetically and comapre each element
      const sortedMatchedPermissions = matchedPermissions.sort();
      const sortedRequiredAllPermissions = permissionsRequired.all.sort();
      for (let i = 0; i < sortedRequiredAllPermissions.length; i++) {
        if (sortedMatchedPermissions[i] !== sortedRequiredAllPermissions[i]) {
          hasRequiredAllPermissions = false;
          break;
        }
      }
    }
  }

  if (permissionsRequired.some) {
    const matchedPermissions = permissionsRequired.some.filter((p) =>
      permissionsAvailable.includes(p),
    );
    hasRequiredAnyPermissions = matchedPermissions.length > 0;
  }

  return hasRequiredAllPermissions && hasRequiredAnyPermissions;
}
